import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Heart, Sparkles, ListChecks, MessageSquarePlus, ShieldCheck, Calendar, Check } from "lucide-react";

const WEEK_ONE = [
  { day: "Day 1", icon: Compass, text: "Create your account and add one habit you'd like to start or stop. Keep it small — the app grows the goal with you." },
  { day: "Days 1–7", icon: Heart, text: "Do a wellbeing check-in each day (about 60 seconds). Slide the scales honestly; there are no wrong answers and nothing is ever penalized." },
  { day: "Day 2+", icon: Sparkles, text: "When you complete your habit, tap \"Mark done\" and then \"Get a reward\". Rate the reward so it learns what actually feels good to you." },
  { day: "Day 3+", icon: ListChecks, text: "Add one custom tracker — sleep hours, medication, caffeine, urges — anything you're curious about." },
  { day: "Day 7", icon: Calendar, text: "Open your weekly digest and \"Patterns we're noticing\". Tell us whether it felt true to your week." },
];

const ASK = [
  "Did anything feel confusing, slow, or broken? (phone or computer — tell us which)",
  "Did the rewards feel personal and kind, or generic?",
  "Was the daily check-in too long, too short, or just right?",
  "Anything you wish it tracked or reminded you about?",
];

export default function Beta() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const registerTo = code ? `/register?code=${code}` : "/register";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 relative z-10" data-testid="beta-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Private beta · Rose City Therapeutics</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-stone-900 mt-2 leading-tight">You're invited to try Root & Rose.</h1>
        <p className="text-base md:text-lg text-stone-600 mt-4 leading-relaxed">
          Thank you for helping test this. Root & Rose helps you start or stop habits using <span className="font-semibold text-stone-800">positive feedback only</span> — personalized rewards, gentle reminders, and a daily wellbeing check-in your coach can follow. It's free during the beta, and your honest feedback shapes what it becomes.
        </p>
        {code && (
          <div className="mt-5 warm-card p-4 flex items-center gap-3 text-sm" data-testid="beta-code-card">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <div>Your coach's invite code <span className="font-mono font-bold text-amber-800">{code}</span> will be filled in for you — you'll be linked automatically.</div>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={registerTo} data-testid="beta-register-link" className="amber-btn">Create my account</Link>
          <Link to="/login" data-testid="beta-login-link" className="px-5 py-3 rounded-full text-sm font-semibold bg-stone-100 hover:bg-stone-200 text-stone-800">I already have one</Link>
        </div>
      </motion.div>

      <section className="mt-12">
        <h2 className="font-display text-base md:text-lg font-bold text-stone-900 uppercase tracking-wide">Your first week</h2>
        <ol className="mt-4 space-y-3">
          {WEEK_ONE.map(({ day, icon: Icon, text }, i) => (
            <motion.li key={day} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }} className="warm-card p-4 flex items-start gap-4" data-testid={`beta-step-${i}`}>
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{day}</div>
                <div className="text-sm text-stone-700 mt-0.5 leading-relaxed">{text}</div>
              </div>
            </motion.li>
          ))}
        </ol>
      </section>

      <section className="mt-10 grid md:grid-cols-2 gap-4">
        <div className="warm-card p-5">
          <div className="flex items-center gap-2 mb-2"><MessageSquarePlus className="w-4 h-4 text-amber-700" /><h3 className="font-display font-bold text-stone-900">What we'd love to hear</h3></div>
          <ul className="space-y-1.5">
            {ASK.map((q) => <li key={q} className="text-sm text-stone-700 flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{q}</li>)}
          </ul>
          <p className="text-xs text-stone-500 mt-3">Use the <span className="font-semibold">Beta feedback</span> button in the bottom-left corner of any screen — it takes 20 seconds.</p>
        </div>
        <div className="warm-card p-5">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /><h3 className="font-display font-bold text-stone-900">Good to know</h3></div>
          <ul className="space-y-1.5 text-sm text-stone-700">
            <li>· It's a beta: expect small rough edges, and tell us about them.</li>
            <li>· Your data is private; only a coach you link to can see it. <Link to="/privacy" className="underline">Privacy notice</Link>.</li>
            <li>· Missing a day never costs you anything — there are no penalties, ever.</li>
            <li>· This is not a crisis service. If you're in danger, call or text 988, or call 911.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
